// ---------------------------------------------------------------------------
// Offline Store — Zustand reactive state for PWA offline mode
// ---------------------------------------------------------------------------
import { create } from 'zustand';

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';
export type SwUpdatePhase = 'idle' | 'available' | 'updating' | 'ready';

interface PrecacheProgress {
  total: number;
  loaded: number;
}

interface OfflineState {
  isOnline: boolean;
  swRegistered: boolean;
  queueCount: number;
  syncStatus: SyncStatus;
  lastSyncAt: number | null;
  installPromptAvailable: boolean;
  swUpdatePhase: SwUpdatePhase;
  swUpdateVersion: string | null;
  swPrecacheProgress: PrecacheProgress | null;
  setOnline: (online: boolean) => void;
  setSwRegistered: (registered: boolean) => void;
  setQueueCount: (count: number) => void;
  setSyncStatus: (status: SyncStatus) => void;
  setLastSyncAt: (ts: number) => void;
  setInstallPromptAvailable: (available: boolean) => void;
  setSwUpdatePhase: (phase: SwUpdatePhase) => void;
  setSwUpdateVersion: (version: string | null) => void;
  setSwPrecacheProgress: (progress: PrecacheProgress | null) => void;
  incrementQueue: () => void;
  decrementQueue: () => void;
}

export const useOfflineStore = create<OfflineState>((set) => ({
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  swRegistered: false,
  queueCount: 0,
  syncStatus: 'idle',
  lastSyncAt: null,
  installPromptAvailable: false,
  swUpdatePhase: 'idle',
  swUpdateVersion: null,
  swPrecacheProgress: null,
  setOnline: (online) => set({ isOnline: online }),
  setSwRegistered: (registered) => set({ swRegistered: registered }),
  setQueueCount: (count) => set({ queueCount: count }),
  setSyncStatus: (status) => set({ syncStatus: status }),
  setLastSyncAt: (ts) => set({ lastSyncAt: ts }),
  setInstallPromptAvailable: (available) => set({ installPromptAvailable: available }),
  setSwUpdatePhase: (phase) => set({ swUpdatePhase: phase }),
  setSwUpdateVersion: (version) => set({ swUpdateVersion: version }),
  setSwPrecacheProgress: (progress) => set({ swPrecacheProgress: progress }),
  incrementQueue: () => set((s) => ({ queueCount: s.queueCount + 1 })),
  decrementQueue: () => set((s) => ({ queueCount: Math.max(0, s.queueCount - 1) })),
}));
